import { useState, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Upload, FileSpreadsheet, AlertTriangle, CheckCircle, ArrowLeft,
  Download, Clock, Users, Package, ChevronRight, RefreshCw,
} from 'lucide-react';
import clsx from 'clsx';
import { Card } from '../shared/components/Card.js';
import { Button } from '../shared/components/Button.js';
import { apiFetch } from '../shared/hooks/useApi.js';
import { useToast } from '../shared/components/Toast.js';
import { useImportWinkel, ImportWinkelBanner } from '../shared/components/ImportWinkelGuard.js';

// ─── Types ────────────────────────────────────────────────────────────────────

type ImportType = 'dossiers' | 'apparaten';
type Step = 'type' | 'upload' | 'mapping' | 'preview' | 'result';

interface ColumnMapping {
  sourceCol: string;
  targetField: string;
}

interface DossierImportRow {
  rij: number;
  klantNaam: string;
  adres: string;
  postcode: string;
  stad: string;
  telefoon: string;
  email: string;
  bestelbonNr: string;
  verkoper: string;
  status: string;
  errors: string[];
  valid: boolean;
  duplicaat: boolean;
}

interface ApparaatImportRow {
  rij: number;
  bestelbonNr: string;
  naam: string;
  merk: string;
  leverancier: string;
  serieNummer: string;
  dossierId: string | null;
  klantNaam: string | null;
  errors: string[];
  valid: boolean;
}

interface ValidationResult<T> {
  validated: T[];
  totaal: number;
  geldig: number;
  ongeldig: number;
  duplicaten?: number;
}

interface ImportResult {
  aangemaakt: number;
  overgeslagen: number;
  totaal: number;
  resultaten: { rij: number; naam: string; status: 'aangemaakt' | 'overgeslagen'; reden?: string; dossierNummer?: string }[];
}

interface ImportLogEntry {
  id: string;
  bestandsnaam: string | null;
  status: string;
  resultaat: { type?: string; aangemaakt: number; overgeslagen: number; totaal: number } | null;
  createdAt: string;
}

// ─── Field definitions ────────────────────────────────────────────────────────

const DOSSIER_FIELDS: { key: string; label: string; required: boolean; hint: string }[] = [
  { key: 'klantNaam', label: 'Klantnaam', required: true, hint: 'bv. "naam", "klant", "klant naam"' },
  { key: 'adres', label: 'Adres', required: false, hint: 'bv. "adres", "straat"' },
  { key: 'postcode', label: 'Postcode', required: false, hint: 'bv. "postcode", "zip"' },
  { key: 'stad', label: 'Gemeente/Stad', required: false, hint: 'bv. "stad", "gemeente", "city"' },
  { key: 'telefoon', label: 'Telefoon', required: false, hint: 'bv. "telefoon", "tel", "gsm"' },
  { key: 'email', label: 'E-mail', required: false, hint: 'bv. "email", "e-mail"' },
  { key: 'bestelbonNr', label: 'Bestelbonnummer', required: false, hint: 'bv. "bestelbon", "order", "bestelbonnr"' },
  { key: 'verkoper', label: 'Verkoper', required: false, hint: 'bv. "verkoper", "sales"' },
  { key: 'status', label: 'Status', required: false, hint: 'actief / afgerond / geannuleerd' },
];

const APPARAAT_FIELDS: { key: string; label: string; required: boolean; hint: string }[] = [
  { key: 'bestelbonNr', label: 'Bestelbonnummer', required: true, hint: 'Moet overeenkomen met bestaand dossier' },
  { key: 'naam', label: 'Naam apparaat', required: true, hint: 'bv. "naam", "omschrijving", "apparaat"' },
  { key: 'merk', label: 'Merk', required: false, hint: 'bv. "merk", "brand"' },
  { key: 'leverancier', label: 'Leverancier', required: false, hint: 'bv. "leverancier", "supplier"' },
  { key: 'serieNummer', label: 'Serienummer', required: false, hint: 'bv. "serie", "serienr", "serial"' },
];

// ─── Auto-detect column mapping ───────────────────────────────────────────────

function autoDetectMapping(cols: string[], importType: ImportType): ColumnMapping[] {
  const fields = importType === 'dossiers' ? DOSSIER_FIELDS : APPARAAT_FIELDS;
  const used = new Set<string>();

  return fields.map(field => {
    const keywords: Record<string, string[]> = {
      // Dossier
      klantNaam: ['klant', 'naam', 'name', 'client', 'customer'],
      adres: ['adres', 'address', 'straat', 'street'],
      postcode: ['postcode', 'zip', 'postal'],
      stad: ['stad', 'gemeente', 'city', 'town', 'place'],
      telefoon: ['telefoon', 'tel', 'gsm', 'mobile', 'phone'],
      email: ['email', 'e-mail', 'mail'],
      bestelbonNr: ['bestelbon', 'bestelnr', 'order', 'dossier', 'ref'],
      verkoper: ['verkoper', 'sales', 'seller', 'medewerker'],
      status: ['status', 'staat'],
      // Apparaat
      naam: ['naam', 'omschrijving', 'beschrijving', 'apparaat', 'toestel', 'product', 'name'],
      merk: ['merk', 'brand', 'fabrikant', 'manufacturer'],
      leverancier: ['leverancier', 'supplier', 'vendor'],
      serieNummer: ['serie', 'serienr', 'serial', 'sn'],
    };

    const hints = keywords[field.key] || [field.key.toLowerCase()];
    const match = cols.find(col => {
      if (used.has(col)) return false;
      const lower = col.toLowerCase().trim();
      return hints.some(h => lower.includes(h));
    });

    if (match) used.add(match);
    return { sourceCol: match || '', targetField: field.key };
  });
}

// ─── Parse file client-side ───────────────────────────────────────────────────

async function parseFile(file: File): Promise<{ columns: string[]; rows: Record<string, string>[] }> {
  const XLSX = await import('xlsx');
  const data = await file.arrayBuffer();
  const wb = XLSX.read(data, { type: 'array' });
  const sheet = wb.Sheets[wb.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json<Record<string, string>>(sheet, { defval: '' });
  const columns = rows.length > 0 ? Object.keys(rows[0]) : [];
  return { columns, rows };
}

// ─── Apply mapping to raw rows ────────────────────────────────────────────────

function applyMapping(rawRows: Record<string, string>[], mapping: ColumnMapping[]): Record<string, string>[] {
  return rawRows.map(row => {
    const mapped: Record<string, string> = {};
    for (const m of mapping) {
      if (m.targetField && m.sourceCol) {
        mapped[m.targetField] = String(row[m.sourceCol] || '').trim();
      }
    }
    return mapped;
  });
}

// ─── Template download ────────────────────────────────────────────────────────

async function downloadTemplate(type: ImportType) {
  const XLSX = await import('xlsx');
  const wb = XLSX.utils.book_new();

  if (type === 'dossiers') {
    const data = [
      {
        Klantnaam: 'Jan Janssen',
        Adres: 'Kerkstraat 12',
        Postcode: '2000',
        Gemeente: 'Antwerpen',
        Telefoon: '0472 12 34 56',
        Email: 'jan.janssen@email.be',
        Bestelbonnummer: '250011/9',
        Verkoper: 'Marie Peeters',
        Status: 'actief',
      },
      {
        Klantnaam: 'Sophie Maes',
        Adres: 'Dorpsstraat 5',
        Postcode: '3000',
        Gemeente: 'Leuven',
        Telefoon: '0487 65 43 21',
        Email: 'sophie.maes@email.be',
        Bestelbonnummer: '250012/3',
        Verkoper: 'Tom Vermeersch',
        Status: 'actief',
      },
    ];
    const ws = XLSX.utils.json_to_sheet(data);
    ws['!cols'] = [
      { wch: 20 }, { wch: 20 }, { wch: 10 }, { wch: 16 },
      { wch: 16 }, { wch: 24 }, { wch: 14 }, { wch: 18 }, { wch: 12 },
    ];
    // Add instruction row comment
    XLSX.utils.book_append_sheet(wb, ws, 'Dossiers');

    // Instructions sheet
    const instrData = [
      { Kolom: 'Klantnaam', Verplicht: 'JA', Uitleg: 'Volledige naam van de klant' },
      { Kolom: 'Adres', Verplicht: 'nee', Uitleg: 'Straatnaam + huisnummer' },
      { Kolom: 'Postcode', Verplicht: 'nee', Uitleg: 'Belgische postcode (4 cijfers)' },
      { Kolom: 'Gemeente', Verplicht: 'nee', Uitleg: 'Stad of gemeente' },
      { Kolom: 'Telefoon', Verplicht: 'nee', Uitleg: 'Mobiel of vast nummer' },
      { Kolom: 'Email', Verplicht: 'nee', Uitleg: 'E-mailadres klant' },
      { Kolom: 'Bestelbonnummer', Verplicht: 'nee', Uitleg: 'Uniek bestelbonnummer (bv. 250011/9)' },
      { Kolom: 'Verkoper', Verplicht: 'nee', Uitleg: 'Naam van de verantwoordelijke verkoper' },
      { Kolom: 'Status', Verplicht: 'nee', Uitleg: 'actief / afgerond / geannuleerd (standaard: actief)' },
    ];
    const wsInstr = XLSX.utils.json_to_sheet(instrData);
    wsInstr['!cols'] = [{ wch: 18 }, { wch: 10 }, { wch: 45 }];
    XLSX.utils.book_append_sheet(wb, wsInstr, 'Instructies');

    XLSX.writeFile(wb, 'dossiers-import-template.xlsx');
  } else {
    const data = [
      { Bestelbonnummer: '250011/9', Naam: 'Inbouwoven AEG', Merk: 'AEG', Leverancier: 'Vimoda', Serienummer: '' },
      { Bestelbonnummer: '250011/9', Naam: 'Vaatwasser Siemens', Merk: 'SIEMENS', Leverancier: 'Vimoda', Serienummer: '' },
      { Bestelbonnummer: '250012/3', Naam: 'Koelkast Bosch', Merk: 'BOSCH', Leverancier: 'BSH', Serienummer: 'SN1234567' },
    ];
    const ws = XLSX.utils.json_to_sheet(data);
    ws['!cols'] = [{ wch: 16 }, { wch: 28 }, { wch: 12 }, { wch: 16 }, { wch: 16 }];
    XLSX.utils.book_append_sheet(wb, ws, 'Apparaten');

    const instrData = [
      { Kolom: 'Bestelbonnummer', Verplicht: 'JA', Uitleg: 'Moet overeenkomen met bestaand dossier (bestelbonnr of dossiernummer)' },
      { Kolom: 'Naam', Verplicht: 'JA', Uitleg: 'Naam of omschrijving van het apparaat' },
      { Kolom: 'Merk', Verplicht: 'nee', Uitleg: 'Merk of fabrikant (bv. AEG, SIEMENS, BOSCH)' },
      { Kolom: 'Leverancier', Verplicht: 'nee', Uitleg: 'Naam van de leverancier' },
      { Kolom: 'Serienummer', Verplicht: 'nee', Uitleg: 'Serienummer van het apparaat' },
    ];
    const wsInstr = XLSX.utils.json_to_sheet(instrData);
    wsInstr['!cols'] = [{ wch: 18 }, { wch: 10 }, { wch: 55 }];
    XLSX.utils.book_append_sheet(wb, wsInstr, 'Instructies');

    XLSX.writeFile(wb, 'apparaten-import-template.xlsx');
  }
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function KlantenImportPage() {
  const winkel = useImportWinkel();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [step, setStep] = useState<Step>('type');
  const [importType, setImportType] = useState<ImportType>('dossiers');
  const [fileName, setFileName] = useState('');
  const [columns, setColumns] = useState<string[]>([]);
  const [rawRows, setRawRows] = useState<Record<string, string>[]>([]);
  const [mapping, setMapping] = useState<ColumnMapping[]>([]);
  const [dossierValidation, setDossierValidation] = useState<ValidationResult<DossierImportRow> | null>(null);
  const [apparaatValidation, setApparaatValidation] = useState<ValidationResult<ApparaatImportRow> | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [showOnlyErrors, setShowOnlyErrors] = useState(false);

  // Import logs
  const { data: logs } = useQuery({
    queryKey: ['klanten-import-logs'],
    queryFn: () => apiFetch<ImportLogEntry[]>('/api/import/klanten/logs'),
  });

  // Validate dossiers
  const validateDossiers = useMutation({
    mutationFn: (rows: Record<string, string>[]) =>
      apiFetch<ValidationResult<DossierImportRow>>('/api/import/klanten/validate/dossiers', {
        method: 'POST',
        body: JSON.stringify({ rows }),
      }),
    onSuccess: (data) => {
      setDossierValidation(data);
      setStep('preview');
    },
    onError: () => toast('error', 'Fout bij valideren bestand'),
  });

  // Validate apparaten
  const validateApparaten = useMutation({
    mutationFn: (rows: Record<string, string>[]) =>
      apiFetch<ValidationResult<ApparaatImportRow>>('/api/import/klanten/validate/apparaten', {
        method: 'POST',
        body: JSON.stringify({ rows }),
      }),
    onSuccess: (data) => {
      setApparaatValidation(data);
      setStep('preview');
    },
    onError: () => toast('error', 'Fout bij valideren bestand'),
  });

  // Confirm dossiers
  const confirmDossiers = useMutation({
    mutationFn: (rows: DossierImportRow[]) =>
      apiFetch<ImportResult>('/api/import/klanten/confirm/dossiers', {
        method: 'POST',
        body: JSON.stringify({ rows }),
      }),
    onSuccess: (data) => {
      setImportResult(data);
      setStep('result');
      queryClient.invalidateQueries({ queryKey: ['klanten-import-logs'] });
    },
    onError: () => toast('error', 'Fout bij importeren'),
  });

  // Confirm apparaten
  const confirmApparaten = useMutation({
    mutationFn: (rows: ApparaatImportRow[]) =>
      apiFetch<ImportResult>('/api/import/klanten/confirm/apparaten', {
        method: 'POST',
        body: JSON.stringify({ rows }),
      }),
    onSuccess: (data) => {
      setImportResult(data);
      setStep('result');
      queryClient.invalidateQueries({ queryKey: ['klanten-import-logs'] });
    },
    onError: () => toast('error', 'Fout bij importeren'),
  });

  const isValidating = validateDossiers.isPending || validateApparaten.isPending;
  const isConfirming = confirmDossiers.isPending || confirmApparaten.isPending;
  const currentValidation = importType === 'dossiers' ? dossierValidation : apparaatValidation;
  const fields = importType === 'dossiers' ? DOSSIER_FIELDS : APPARAAT_FIELDS;

  // File handler
  const handleFile = useCallback(async (file: File) => {
    if (!winkel.isBevestigd()) { toast('error', `Bevestig eerst bovenaan dat je in ${winkel.winkelNaam} importeert`); return; }
    if (!file.name.match(/\.(xlsx?|csv)$/i)) {
      toast('error', 'Alleen .xlsx, .xls of .csv bestanden');
      return;
    }
    try {
      setFileName(file.name);
      const { columns: cols, rows } = await parseFile(file);
      if (rows.length === 0) {
        toast('error', 'Geen rijen gevonden in bestand');
        return;
      }
      setColumns(cols);
      setRawRows(rows);
      const detected = autoDetectMapping(cols, importType);
      setMapping(detected);
      setStep('mapping');
    } catch {
      toast('error', 'Fout bij lezen bestand');
    }
  }, [toast, importType]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleMappingChange = (fieldKey: string, sourceCol: string) => {
    setMapping(prev => prev.map(m => m.targetField === fieldKey ? { ...m, sourceCol } : m));
  };

  const handleConfirmMapping = () => {
    const mapped = applyMapping(rawRows, mapping);
    if (importType === 'dossiers') {
      validateDossiers.mutate(mapped);
    } else {
      validateApparaten.mutate(mapped);
    }
  };

  const handleConfirmImport = () => {
    if (importType === 'dossiers' && dossierValidation) {
      confirmDossiers.mutate(dossierValidation.validated.filter(r => r.valid));
    } else if (importType === 'apparaten' && apparaatValidation) {
      confirmApparaten.mutate(apparaatValidation.validated.filter(r => r.valid));
    }
  };

  const resetAll = () => {
    setStep('type');
    setFileName('');
    setColumns([]);
    setRawRows([]);
    setMapping([]);
    setDossierValidation(null);
    setApparaatValidation(null);
    setImportResult(null);
    setShowOnlyErrors(false);
  };

  const goBackToUpload = () => {
    setStep('upload');
    setFileName('');
    setColumns([]);
    setRawRows([]);
    setMapping([]);
    setDossierValidation(null);
    setApparaatValidation(null);
    setShowOnlyErrors(false);
  };

  const displayedDossierRows = useMemo(() => {
    if (!dossierValidation) return [];
    return showOnlyErrors ? dossierValidation.validated.filter(r => !r.valid) : dossierValidation.validated;
  }, [dossierValidation, showOnlyErrors]);

  const displayedApparaatRows = useMemo(() => {
    if (!apparaatValidation) return [];
    return showOnlyErrors ? apparaatValidation.validated.filter(r => !r.valid) : apparaatValidation.validated;
  }, [apparaatValidation, showOnlyErrors]);

  // ─── Step: Type selector ───────────────────────────────────────────────────
  if (step === 'type') {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Klantdata importeren</h1>
          <p className="text-sm text-gray-500 mt-1">Importeer bestaande klantdata vanuit Excel of CSV</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={() => { setImportType('dossiers'); setStep('upload'); }}
            className="group p-6 bg-white border-2 border-gray-200 rounded-xl text-left hover:border-gold/50 hover:bg-gold/5 transition-all"
          >
            <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center mb-4 group-hover:bg-blue-100 transition-colors">
              <Users size={24} className="text-blue-600" />
            </div>
            <h3 className="text-base font-semibold text-gray-900 mb-1">Klanten & Dossiers</h3>
            <p className="text-sm text-gray-500">
              Importeer klantgegevens: naam, adres, contactinfo, bestelbonnummer en status.
              Elk rij wordt een nieuw dossier.
            </p>
            <div className="flex items-center gap-1 mt-4 text-xs text-gold font-medium">
              Selecteren <ChevronRight size={14} />
            </div>
          </button>

          <button
            onClick={() => { setImportType('apparaten'); setStep('upload'); }}
            className="group p-6 bg-white border-2 border-gray-200 rounded-xl text-left hover:border-gold/50 hover:bg-gold/5 transition-all"
          >
            <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center mb-4 group-hover:bg-amber-100 transition-colors">
              <Package size={24} className="text-amber-600" />
            </div>
            <h3 className="text-base font-semibold text-gray-900 mb-1">Apparaten</h3>
            <p className="text-sm text-gray-500">
              Importeer apparaten gekoppeld aan bestaande dossiers via het bestelbonnummer. Naam, merk, leverancier en serienummer.
            </p>
            <div className="flex items-center gap-1 mt-4 text-xs text-gold font-medium">
              Selecteren <ChevronRight size={14} />
            </div>
          </button>
        </div>

        {/* Recent import logs */}
        {logs && logs.length > 0 && (
          <Card>
            <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Clock size={16} className="text-gray-400" /> Recente imports
            </h3>
            <div className="space-y-2">
              {logs.slice(0, 6).map(log => {
                const r = log.resultaat as any;
                return (
                  <div key={log.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <div className="flex items-center gap-2">
                      <span className={clsx('w-2 h-2 rounded-full', log.status === 'SUCCES' ? 'bg-green-500' : 'bg-red-500')} />
                      <span className="text-sm text-gray-700">{log.bestandsnaam}</span>
                      {r?.type && (
                        <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded">{r.type}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      {r && <span>{r.aangemaakt}/{r.totaal} aangemaakt</span>}
                      <span>{new Date(log.createdAt).toLocaleDateString('nl-BE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}
      </div>
    );
  }

  // ─── Step: Upload ──────────────────────────────────────────────────────────
  if (step === 'upload') {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <button onClick={resetAll} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <ArrowLeft size={18} className="text-gray-500" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">
              {importType === 'dossiers' ? 'Klanten & Dossiers' : 'Apparaten'} importeren
            </h1>
            <p className="text-sm text-gray-500">Stap 1 van 3 — Bestand uploaden</p>
          </div>
        </div>

      <ImportWinkelBanner guard={winkel} />
        {/* Upload zone */}
        <Card>
          <div
            onDrop={handleDrop}
            onDragOver={e => e.preventDefault()}
            className="border-2 border-dashed border-gray-300 rounded-xl p-12 text-center hover:border-gold/50 transition-colors"
          >
            <Upload size={40} className="text-gray-300 mx-auto mb-4" />
            <p className="text-sm text-gray-600 mb-2">Sleep een Excel of CSV bestand hierheen</p>
            <p className="text-xs text-gray-400 mb-4">of</p>
            <label className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-gold rounded-lg hover:bg-gold/90 transition-colors cursor-pointer">
              <FileSpreadsheet size={16} />
              Kies bestand
              <input type="file" accept=".xlsx,.xls,.csv" onChange={handleInputChange} className="hidden" />
            </label>
            <p className="text-xs text-gray-400 mt-3">.xlsx, .xls of .csv — maximaal 5000 rijen</p>
          </div>
        </Card>

        {/* Template download */}
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Template downloaden</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {importType === 'dossiers'
                  ? 'Met voorbeelddata voor: Klantnaam, Adres, Postcode, Gemeente, Telefoon, Email, Bestelbonnummer, Verkoper, Status'
                  : 'Met voorbeelddata voor: Bestelbonnummer, Naam, Merk, Leverancier, Serienummer'}
              </p>
            </div>
            <button
              onClick={() => downloadTemplate(importType)}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Download size={16} />
              Template ({importType === 'dossiers' ? 'dossiers' : 'apparaten'})
            </button>
          </div>
        </Card>

        {/* Supported columns info */}
        <Card>
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Verwachte kolommen</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {fields.map(f => (
              <div key={f.key} className="flex items-start gap-2 text-xs">
                <span className={clsx(
                  'mt-0.5 w-1.5 h-1.5 rounded-full flex-shrink-0',
                  f.required ? 'bg-red-400' : 'bg-gray-300'
                )} />
                <div>
                  <span className="font-medium text-gray-700">{f.label}</span>
                  {f.required && <span className="text-red-500 ml-0.5">*</span>}
                  <p className="text-gray-400">{f.hint}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-3"><span className="text-red-500">*</span> Verplicht veld</p>
        </Card>
      </div>
    );
  }

  // ─── Step: Mapping ─────────────────────────────────────────────────────────
  if (step === 'mapping') {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <button onClick={goBackToUpload} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <ArrowLeft size={18} className="text-gray-500" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Kolommen koppelen</h1>
            <p className="text-sm text-gray-500">Stap 2 van 3 — <span className="font-medium">{fileName}</span> · {rawRows.length} rijen gevonden</p>
          </div>
        </div>

        <Card>
          <p className="text-sm text-gray-600 mb-4">
            We hebben de kolommen automatisch gekoppeld. Controleer de mapping en pas aan waar nodig.
          </p>
          <div className="space-y-3">
            {fields.map(field => {
              const currentMapping = mapping.find(m => m.targetField === field.key);
              const currentSourceCol = currentMapping?.sourceCol || '';
              return (
                <div key={field.key} className="grid grid-cols-5 items-center gap-3">
                  {/* Target field */}
                  <div className="col-span-2">
                    <span className="text-sm font-medium text-gray-900">{field.label}</span>
                    {field.required && <span className="text-red-500 ml-1 text-xs">*</span>}
                    <p className="text-xs text-gray-400">{field.hint}</p>
                  </div>
                  {/* Arrow */}
                  <div className="flex items-center justify-center text-gray-400">
                    <ChevronRight size={16} />
                  </div>
                  {/* Source column select */}
                  <div className="col-span-2">
                    <select
                      value={currentSourceCol}
                      onChange={e => handleMappingChange(field.key, e.target.value)}
                      className={clsx(
                        'w-full text-sm border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gold/30',
                        currentSourceCol ? 'border-green-300 bg-green-50' : 'border-gray-300',
                        field.required && !currentSourceCol && 'border-red-300 bg-red-50'
                      )}
                    >
                      <option value="">— Niet gekoppeld —</option>
                      {columns.map(col => (
                        <option key={col} value={col}>{col}</option>
                      ))}
                    </select>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Preview of first 3 rows */}
        <Card>
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Voorbeeld (eerste 3 rijen)</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-gray-200">
                  {fields.map(f => {
                    const m = mapping.find(x => x.targetField === f.key);
                    return (
                      <th key={f.key} className="text-left px-3 py-2 text-gray-500 font-medium">
                        {f.label}
                        {m?.sourceCol && <div className="text-gray-400 font-normal">← {m.sourceCol}</div>}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {rawRows.slice(0, 3).map((row, i) => (
                  <tr key={i} className="border-b border-gray-100">
                    {fields.map(f => {
                      const m = mapping.find(x => x.targetField === f.key);
                      const val = m?.sourceCol ? row[m.sourceCol] || '' : '';
                      return (
                        <td key={f.key} className={clsx('px-3 py-2', val ? 'text-gray-700' : 'text-gray-300')}>
                          {val || '—'}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <div className="flex items-center justify-between">
          <Button variant="secondary" onClick={goBackToUpload}>
            <ArrowLeft size={16} /> Terug
          </Button>
          <Button
            onClick={handleConfirmMapping}
            disabled={isValidating}
          >
            {isValidating ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Valideren...
              </>
            ) : (
              <>
                <CheckCircle size={16} />
                Valideren & voorvertonen ({rawRows.length} rijen)
              </>
            )}
          </Button>
        </div>
      </div>
    );
  }

  // ─── Step: Preview ─────────────────────────────────────────────────────────
  if (step === 'preview' && currentValidation) {
    return (
      <div className="max-w-5xl mx-auto space-y-4">
        <div className="flex items-center gap-3">
          <button onClick={() => setStep('mapping')} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <ArrowLeft size={18} className="text-gray-500" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Voorvertoning & validatie</h1>
            <p className="text-sm text-gray-500">Stap 3 van 3 — Controleer de data voor import</p>
          </div>
        </div>

        {/* Summary */}
        <div className="flex items-center gap-4 flex-wrap">
          <span className="text-sm text-gray-600">
            <span className="font-semibold">{fileName}</span> — {currentValidation.totaal} rijen
          </span>
          <div className="flex items-center gap-3">
            {currentValidation.geldig > 0 && (
              <span className="flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 px-2 py-1 rounded-full">
                <CheckCircle size={12} /> {currentValidation.geldig} geldig
              </span>
            )}
            {currentValidation.ongeldig > 0 && (
              <span className="flex items-center gap-1 text-xs font-medium text-red-700 bg-red-50 px-2 py-1 rounded-full">
                <AlertTriangle size={12} /> {currentValidation.ongeldig} fouten
              </span>
            )}
            {importType === 'dossiers' && (currentValidation as ValidationResult<DossierImportRow>).duplicaten! > 0 && (
              <span className="flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 px-2 py-1 rounded-full">
                <AlertTriangle size={12} /> {(currentValidation as ValidationResult<DossierImportRow>).duplicaten} duplicaten
              </span>
            )}
          </div>
        </div>

        {/* Filter */}
        {currentValidation.ongeldig > 0 && (
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer w-fit">
            <input
              type="checkbox"
              checked={showOnlyErrors}
              onChange={e => setShowOnlyErrors(e.target.checked)}
              className="rounded border-gray-300 text-gold focus:ring-gold"
            />
            Toon alleen fouten
          </label>
        )}

        {/* Preview table */}
        <Card padding={false}>
          <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
            {importType === 'dossiers' ? (
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white z-10">
                  <tr className="border-b border-gray-200">
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-3 py-2 w-8">#</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-3 py-2">Status</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-3 py-2">Klantnaam</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-3 py-2">Adres</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-3 py-2">Telefoon</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-3 py-2">Email</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-3 py-2">Bestelbon</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedDossierRows.map(row => (
                    <tr key={row.rij} className={clsx('border-b border-gray-100', !row.valid && 'bg-red-50/50')}>
                      <td className="px-3 py-2 text-xs text-gray-400">{row.rij}</td>
                      <td className="px-3 py-2">
                        {row.valid ? (
                          <CheckCircle size={14} className="text-green-500" />
                        ) : (
                          <div className="flex items-center gap-1">
                            <AlertTriangle size={14} className="text-red-500 flex-shrink-0" />
                            <span className="text-[11px] text-red-600 truncate max-w-[140px]" title={row.errors.join(', ')}>
                              {row.errors[0]}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 font-medium text-gray-900">{row.klantNaam || <span className="text-gray-300">—</span>}</td>
                      <td className="px-3 py-2 text-gray-600 text-xs">
                        {[row.adres, row.postcode, row.stad].filter(Boolean).join(', ') || <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-3 py-2 text-gray-600">{row.telefoon || <span className="text-gray-300">—</span>}</td>
                      <td className="px-3 py-2 text-gray-600">{row.email || <span className="text-gray-300">—</span>}</td>
                      <td className="px-3 py-2 text-gray-600">{row.bestelbonNr || <span className="text-gray-300">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white z-10">
                  <tr className="border-b border-gray-200">
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-3 py-2 w-8">#</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-3 py-2">Status</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-3 py-2">Bestelbon</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-3 py-2">Klant</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-3 py-2">Naam apparaat</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-3 py-2">Merk</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-3 py-2">Leverancier</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedApparaatRows.map(row => (
                    <tr key={row.rij} className={clsx('border-b border-gray-100', !row.valid && 'bg-red-50/50')}>
                      <td className="px-3 py-2 text-xs text-gray-400">{row.rij}</td>
                      <td className="px-3 py-2">
                        {row.valid ? (
                          <CheckCircle size={14} className="text-green-500" />
                        ) : (
                          <div className="flex items-center gap-1">
                            <AlertTriangle size={14} className="text-red-500 flex-shrink-0" />
                            <span className="text-[11px] text-red-600 truncate max-w-[140px]" title={row.errors.join(', ')}>
                              {row.errors[0]}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 font-medium text-gray-900">{row.bestelbonNr || <span className="text-gray-300">—</span>}</td>
                      <td className="px-3 py-2 text-gray-600">{row.klantNaam || <span className="text-gray-300">—</span>}</td>
                      <td className="px-3 py-2 text-gray-900">{row.naam}</td>
                      <td className="px-3 py-2 text-gray-600">{row.merk || <span className="text-gray-300">—</span>}</td>
                      <td className="px-3 py-2 text-gray-600">{row.leverancier || <span className="text-gray-300">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </Card>

        <div className="flex items-center justify-between">
          <Button variant="secondary" onClick={() => setStep('mapping')}>
            <ArrowLeft size={16} /> Kolommen aanpassen
          </Button>
          <div className="flex items-center gap-3">
            {currentValidation.ongeldig > 0 && (
              <p className="text-xs text-amber-600">
                {currentValidation.ongeldig} ongeldige rij{currentValidation.ongeldig !== 1 ? 'en' : ''} worden overgeslagen
              </p>
            )}
            <Button
              onClick={handleConfirmImport}
              disabled={currentValidation.geldig === 0 || isConfirming}
            >
              {isConfirming ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Importeren...
                </>
              ) : (
                <>
                  <Upload size={16} />
                  {currentValidation.geldig} {importType === 'dossiers' ? 'dossiers' : 'apparaten'} importeren
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Step: Result ──────────────────────────────────────────────────────────
  if (step === 'result' && importResult) {
    return (
      <div className="max-w-4xl mx-auto space-y-4">
        <Card>
          <div className="flex items-center gap-4">
            <div className={clsx(
              'w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0',
              importResult.aangemaakt > 0 ? 'bg-green-100' : 'bg-red-100'
            )}>
              {importResult.aangemaakt > 0
                ? <CheckCircle size={24} className="text-green-600" />
                : <AlertTriangle size={24} className="text-red-600" />}
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Import voltooid</h3>
              <p className="text-sm text-gray-600">
                <span className="font-semibold text-green-600">{importResult.aangemaakt}</span> {importType === 'dossiers' ? 'dossiers' : 'apparaten'} aangemaakt
                {importResult.overgeslagen > 0 && (
                  <>, <span className="text-amber-600">{importResult.overgeslagen}</span> overgeslagen</>
                )}{' '}van {importResult.totaal} rijen
              </p>
            </div>
          </div>
        </Card>

        <Card padding={false}>
          <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white z-10">
                <tr className="border-b border-gray-200">
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-2 w-8">#</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-2">Naam</th>
                  {importType === 'dossiers' && (
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-2">Dossiernummer</th>
                  )}
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-2">Status</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-2">Reden</th>
                </tr>
              </thead>
              <tbody>
                {importResult.resultaten.map(r => (
                  <tr key={r.rij} className={clsx('border-b border-gray-100', r.status === 'overgeslagen' && 'bg-amber-50/50')}>
                    <td className="px-4 py-2 text-xs text-gray-400">{r.rij}</td>
                    <td className="px-4 py-2 text-gray-900">{r.naam}</td>
                    {importType === 'dossiers' && (
                      <td className="px-4 py-2 text-gray-600 font-mono text-xs">{r.dossierNummer || '—'}</td>
                    )}
                    <td className="px-4 py-2">
                      {r.status === 'aangemaakt' ? (
                        <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
                          <CheckCircle size={12} /> Aangemaakt
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
                          <AlertTriangle size={12} /> Overgeslagen
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-500">{r.reden || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <div className="flex items-center justify-between">
          <Button variant="secondary" onClick={resetAll}>
            <ArrowLeft size={16} /> Terug naar overzicht
          </Button>
          <Button variant="secondary" onClick={goBackToUpload}>
            <RefreshCw size={16} /> Nieuw bestand importeren
          </Button>
        </div>
      </div>
    );
  }

  return null;
}
